import './styles.scss';

import { Button, Card, Form, ProgressBar } from 'react-bootstrap';
import { FileUploadStage, GenericFileType, GenericFileUploadOptions } from './new';
import { ForwardRefRenderFunction, forwardRef, useImperativeHandle, useState } from 'react';
import { Validation, bytesToSize, createNotification, getClasses, uuid } from '@/utils';

import { FileUploader } from 'react-drag-drop-files';
import Toast from '@/models/Toast';
import axios from 'axios';
import md5 from 'md5';
import useConfirmation from '@/hooks/useConfirmation';
import useLocale from '@/hooks/useLocale';

type GenericFileUploadProps = {
  options?: GenericFileUploadOptions;
  onSuccess?: () => Promise<any>;
  onError?: () => Promise<any>;
  onCancel?: () => Promise<any>;
  onContinue?: (files) => Promise<any>;
  onChange?: (files: GenericFileType[]) => void;
  locale?: Record<string, string>;
  condensed?: boolean;
  onDelete?: (file: GenericFileType) => void;
};

export interface DeferredFileUploadHandle {
  getFilesToUpload: () => { filename: string; md5: string; fileSize: number; contentType: string }[];
  performDeferredUploads: <T>(presigned: any, silent?: boolean) => Promise<void>;
}

export interface DeferredFileUploadState {
  files: GenericFileType[];
  errors: Record<string, { message: string }>;
  loading: boolean;
}

const DeferredFileUpload: ForwardRefRenderFunction<DeferredFileUploadHandle, GenericFileUploadProps> = (
  { locale: customLocale = {}, options = {}, onError, onSuccess, onContinue, onCancel, condensed = false, onDelete },
  ref
) => {
  const [state, setState] = useState<DeferredFileUploadState>({
    files: [],
    errors: {},
    loading: false,
  });
  const { files, loading } = state;
  const { showFiles = true, label = '', multiple = true, supportedExtensions = [] } = options;
  const locale = useLocale(customLocale);
  const confirmFileDelete = useConfirmation();

  const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const processFileForUpload = async (file: File): Promise<GenericFileType> => {
    try {
      await readFileAsArrayBuffer(file);
      const id = uuid();
      return {
        file,
        md5: md5(file.name, file.size, file.type),
        id,
        progress: 0,
        stage: FileUploadStage.Processing,
      };
    } catch (error) {
      console.error('Failed processing', file.name, error);
      if (onError) await onError();
      throw error;
    }
  };

  const isFileSupported = (file) => {
    const fileExtensionRegex = /[0-9a-z]+$/i;
    return supportedExtensions.length > 0 ? supportedExtensions.includes(file.name.match(fileExtensionRegex)?.[0]) : true;
  };

  const uploadFile = async (processedFile: GenericFileType, url: string, contentType?: string) => {
    try {
      if (!processedFile) throw new Error('Nothing to upload.');
      if (!url) throw new Error('No url provided.');
      if (!isFileSupported(processedFile.file)) throw new Error('File type not supported');
      if (Validation.isNil(processedFile.file.type)) throw new Error('No contentType provided.');
      setState((current) => ({
        ...current,
        files: current.files.map((f) => (f.md5 === processedFile.md5 ? { ...f, progress: 0, stage: FileUploadStage.Uploading } : f)),
      }));
      const headers: any = {
        'Content-Type': contentType ?? processedFile.file.type,
      };
      await axios.put(url, processedFile.file, { headers });
      setState((current) => ({
        ...current,
        files: current.files.map((f) => (f.md5 === processedFile.md5 ? { ...f, progress: 100, stage: FileUploadStage.Done } : f)),
      }));
    } catch (err) {
      setState((current) => ({
        ...current,
        files: current.files.map((f) => (f.md5 === processedFile.md5 ? { ...f, progress: 100, stage: FileUploadStage.Error } : f)),
        errors: { ...current.errors, [processedFile.md5]: { message: err.message } },
      }));
    }
  };

  const handleUpload = async (file: File) => {
    const processedFile = await processFileForUpload(file);
    const alreadyExists = files.some((f) => f.md5 === processedFile.md5);
    if (alreadyExists) {
      createNotification(`File ${file.name} already exists`, Toast.Type.WARNING, 'Lost And Found Issue');
      return;
    }
    setState((current) => ({
      ...current,
      files: [...current.files, processedFile],
    }));
  };

  const handleChange = async (files: File[] | File) => {
    setState((current) => ({ ...current, loading: true }));
    files = multiple ? Object.values(files) : [files];
    try {
      const promises = files.map(async (file) => handleUpload(file));
      await Promise.allSettled(promises);
      if (onSuccess) await onSuccess();
    } catch (e) {
      if (onError) await onError();
    } finally {
      setState((current) => ({ ...current, loading: false }));
    }
  };

  const handleCancel = async () => {
    onCancel?.();
    setState((current) => ({ ...current, files: [] }));
  };

  const handleContinue = async () => {
    onContinue?.(files);
    setState((current) => ({ ...current, files: [] }));
  };

  const getFilesToUpload = () => {
    return files.map((f) => ({
      filename: f.file.name,
      md5: f.md5,
      fileSize: f.file.size,
      contentType: '', // Empty because it's required to create a presigned URL, but will be set later by the server,
    }));
  };

  const performDeferredUploads = async (presigned, silent = false) => {
    for (const { document, presignedUrl } of presigned) {
      const { md5, contentType, filename } = document;
      const f = files.find((x) => x.md5 === md5);
      try {
        if (f) {
          await uploadFile(f, presignedUrl, contentType);
          if (!silent) {
            createNotification(`Sucessfully Uploaded Document ${filename}`, Toast.Type.SUCCESS, 'File Upload');
          }
        }
      } catch (error) {
        console.error(`Error during deferred upload for file ${filename}:`, error);
      }
    }
  };

  const handleDelete = (fileToDelete: GenericFileType) => {
    confirmFileDelete()
      .then(() => {
        setState((current) => ({
          ...current,
          files: current.files.filter((file) => file.md5 !== fileToDelete.md5),
        }));
        onDelete?.(fileToDelete);
      })
      .catch(() => console.log('File deletion cancelled'));
  };

  useImperativeHandle(ref, () => ({
    getFilesToUpload,
    performDeferredUploads,
  }));

  return (
    <div className={getClasses('FileUpload', 'flex flex-column', condensed ? 'condensed' : undefined)}>
      <Form.Group className="mb-3">
        <Form.Label id="file-upload-label">{!(files || []).length && <div>{label}</div>}</Form.Label>
        <FileUploader classes="drag-and-drop border border-primary rounded text-center" handleChange={handleChange} multiple={multiple}>
          <>
            <div className="drag-area d-flex flex-column mb-1">
              <i className="upload-icon fa fa-2x fa-file-lines text-secondary my-2" />
              <div className="m-0 d-flex gap-1 justify-content-center">
                <span>{locale('Drag and Drop or')}</span>
                <strong>{locale('Browse')}</strong>
              </div>
            </div>
            {!condensed && (
              <Button className="mt-2 px-5" name="SUBMIT_UPLOAD">
                {locale('Upload')}
              </Button>
            )}
          </>
        </FileUploader>
      </Form.Group>
      {!condensed && showFiles && (
        <div className="d-flex flex-column gap-3 width-100">
          {files.map((file) => (
            <Card key={file.id} className="GenericFile d-flex flex-row position-relative px-0">
              <Card.Body className="GenericFile-Icon d-flex flex-grow-0 justify-content-center align-items-center">
                <i className="sv sv-file-image fs-1" />
              </Card.Body>
              <Card.Body className="GenericFile-Details d-flex justify-content-center flex-column flex-grow-1 ps-0">
                <div className="d-flex w-100 gap-2">
                  <span className="GenericFile-FileName flex-grow-1 text-primary" title={file?.file?.name}>
                    {file?.file?.name}
                  </span>
                  <span className="GenericFile-FileSize">- {bytesToSize(file?.file?.size)}</span>
                  <span className="GenericFile-Stage text-muted">
                    {locale(file?.stage === FileUploadStage.Processing ? 'WAITING' : file?.stage)}
                  </span>
                </div>
                <ProgressBar
                  now={file?.progress}
                  variant={file?.stage === FileUploadStage.Error ? 'danger' : file?.progress < 100 ? 'primary' : 'success'}
                  style={{ height: '0.25rem' }}
                />
                {file?.stage === FileUploadStage.Processing && (
                  <div className="small text-gray italic">
                    <span className="text-danger bold">* </span>Submitting the form will upload this file
                  </div>
                )}
                <Button
                  className="GenericFile-Delete rounded-circle p-0 d-flex justify-content-center align-items-center"
                  size="sm"
                  variant="primary"
                  onClick={() => handleDelete(file)}
                  style={{ position: 'absolute', top: '-0.75rem', right: '-0.75rem', width: '1.5rem', height: '1.5rem' }}
                >
                  <i className="fa fa-times" />
                </Button>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
      <div className="justify-content-end flex gap-2">
        {onCancel && (
          <Button className="mt-2 px-5" name="CANCEL" onClick={handleCancel} variant="secondary">
            {locale('Cancel')}
          </Button>
        )}
        {onContinue && (
          <Button className="mt-2 px-5" name="CONTINUE" disabled={files.length === 0 || loading} onClick={handleContinue}>
            {locale('Continue')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default forwardRef(DeferredFileUpload);
